import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { HTTPError } from 'ky';

import { useWmgSession } from '~/modules/auth/hooks/useWmgSession';
import {
  LabelGroupQueryKeys,
  LabelQueryKeys,
} from '~settings/api/config/queryKeys';
import { BFFClient } from '~settings/api/lib/BFFClient';
import { getRequestHeaders } from '~settings/api/lib/headers';
import type { LabelGroupDetail } from '~settings/api/lib/schemas';
import type {
  LabelGroupPayload,
  PresetUseMutationOptions,
} from '~settings/api/lib/types';

export const useLabelGroupCreate = (
  options?: PresetUseMutationOptions<LabelGroupDetail, LabelGroupPayload>,
) => {
  const { token } = useWmgSession();
  const queryClient = useQueryClient();

  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation<LabelGroupDetail, HTTPError, LabelGroupPayload>({
    mutationFn: async (data: LabelGroupPayload) => {
      return BFFClient.post('label-groups', {
        json: data,
        headers: getRequestHeaders(token),
      }).json();
    },

    onSuccess: async (data: LabelGroupDetail, variables, ctx) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: LabelGroupQueryKeys.list({}),
        }),
        queryClient.invalidateQueries({
          queryKey: LabelQueryKeys.list({}),
        }),
      ]);

      if (onSuccess) {
        onSuccess(data, variables, ctx);
      }
    },
    onError: (error) => {
      console.error('Create label group failed:', error);
    },
    ...restOptions,
  });
};
